import React, { useState, useEffect } from "react";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { Badge } from "@/components/_components/badge";
import { AGENT_TYPE, AGENT_TYPE_LABELS } from "@/constants/constants";
import { API_CONFIG } from "@/constants/api";
import { customInstance } from "@/lib/axios-instance";
import { trimCommunicationIntegrationFormData } from "@/lib/utils/data-trimmer";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { Plus, Trash2, Settings, FileText, Phone, Hash, Key } from "lucide-react";
import toast from "react-hot-toast";

interface CommunicationIntegration {
  id?: number;
  agentType: string;
  intType: string;
  phoneNo: string;
  countryCode?: string;
  details: Record<string, any>;
  detailsAsString?: string;
}

interface ConfigField {
  key: string;
  name: string;
}

interface ConfigResponse {
  addAttributes: boolean;
  keys: ConfigField[];
}

interface Country {
  countryCode: string;
  isdCode: string;
  countryName: string;
}

interface CustomAttribute {
  key: string;
  value: string;
}

interface CommunicationIntegrationFormProps {
  integration?: CommunicationIntegration | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const INTEGRATION_TYPES = [
  { key: "awsConnect", value: "AWS Connect" },
  { key: "twilio", value: "Twilio" },
];

const CommunicationIntegrationForm: React.FC<CommunicationIntegrationFormProps> = ({
  integration,
  onSuccess,
  onCancel,
}) => {
  const { handleError } = useErrorHandler();
  const [formData, setFormData] = useState({
    agentType: "",
    intType: "",
    countryCode: "",
    isdCode: "",
    phoneNumber: "",
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [configFields, setConfigFields] = useState<ConfigField[]>([]);
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [allowCustomAttributes, setAllowCustomAttributes] = useState(false);
  const [customAttributes, setCustomAttributes] = useState<CustomAttribute[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasLoadedCountries = React.useRef(false);

  // Load countries on component mount only if NOT in edit mode
  useEffect(() => {
    if (!integration && !hasLoadedCountries.current) {
      hasLoadedCountries.current = true;
      loadCountries();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCountries = async () => {
    try {
      setIsLoadingCountries(true);
      const response = await customInstance<Country[]>({
        url: API_CONFIG.ENDPOINTS.COUNTRIES_LIST,
        method: "GET",
      });

      setCountries(response.data || []);

      // Set default country if available and not in edit mode
      if (!integration && response.data && response.data.length > 0) {
        const defaultCountry = response.data.find(c => c.countryCode === "USA") || response.data[0];
        setFormData((prev) => ({
          ...prev,
          countryCode: defaultCountry.countryCode,
          isdCode: defaultCountry.isdCode,
        }));
      }
    } catch (error: any) {
      console.error("Error loading countries:", error);
      handleError(error);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  useEffect(() => {
    if (integration) {
      // In edit mode, just use the countryCode from integration
      // Parse phone number to extract phone number part
      const phoneNo = integration.phoneNo;
      let phoneNumber = phoneNo;
      const countryCode = integration.countryCode || "";

      // Extract just the phone number part by removing any leading + and digits that might be ISD code
      // We'll display the countryCode as-is without needing to parse
      const isdCodeMatch = phoneNo.match(/^(\+\d+)/);
      if (isdCodeMatch) {
        phoneNumber = phoneNo.substring(isdCodeMatch[1].length);
      }

      setFormData({
        agentType: integration.agentType,
        intType: integration.intType,
        countryCode,
        isdCode: isdCodeMatch ? isdCodeMatch[1] : "",
        phoneNumber,
      });

      // Set config values from details
      if (integration.details) {
        // We'll separate predefined and custom attributes after loading config fields
        // For now, just set all values
        setConfigValues(integration.details);
      }

      // Load config fields for the integration type
      if (integration.intType) {
        loadConfigFields(integration.intType);
      }
    }
  }, [integration]);

  const loadConfigFields = async (intType: string) => {
    try {
      setIsLoadingConfig(true);
      const response = await customInstance<ConfigResponse>({
        url: `${API_CONFIG.ENDPOINTS.INTEGRATION_CONFIG}/key/phoneNoMapping/type/${intType}`,
        method: "GET",
      });

      const configResponse = response.data;
      setConfigFields(configResponse?.keys || []);
      setAllowCustomAttributes(configResponse?.addAttributes || false);

      // If editing existing integration, separate predefined and custom attributes
      if (integration?.details) {
        const predefinedKeys = new Set((configResponse?.keys || []).map(field => field.key));
        const customAttrs: CustomAttribute[] = [];
        const predefinedValues: Record<string, string> = {};

        Object.entries(integration.details).forEach(([key, value]) => {
          if (predefinedKeys.has(key)) {
            predefinedValues[key] = value as string;
          } else {
            customAttrs.push({ key, value: value as string });
          }
        });

        setConfigValues(predefinedValues);
        setCustomAttributes(customAttrs);
      }
    } catch (error: any) {
      console.error("Error loading config fields:", error);
      handleError(error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const handleIntTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, intType: value }));
    setConfigFields([]);
    setConfigValues({});
    setCustomAttributes([]);
    setAllowCustomAttributes(false);
    if (value) {
      loadConfigFields(value);
    }
  };

  const handleConfigValueChange = (key: string, value: string) => {
    setConfigValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddCustomAttribute = () => {
    setCustomAttributes((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleRemoveCustomAttribute = (index: number) => {
    setCustomAttributes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCustomAttributeChange = (index: number, field: 'key' | 'value', value: string) => {
    setCustomAttributes((prev) =>
      prev.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr
      )
    );
  };

  const validateForm = () => {
    if (!formData.agentType) {
      toast.error("Agent Type is required");
      return false;
    }
    if (!formData.intType) {
      toast.error("Integration Type is required");
      return false;
    }
    // Skip phone number validation in edit mode since it's read-only
    if (!integration && !formData.phoneNumber.trim()) {
      toast.error("Phone Number is required");
      return false;
    }

    // Validate all predefined config fields are filled
    for (const field of configFields) {
      if (!configValues[field.key]?.trim()) {
        toast.error(`${field.name} is required`);
        return false;
      }
    }

    // Validate custom attributes
    for (let i = 0; i < customAttributes.length; i++) {
      const attr = customAttributes[i];
      if (!attr.key.trim()) {
        toast.error(`Custom attribute ${i + 1} key is required`);
        return false;
      }
      if (!attr.value.trim()) {
        toast.error(`Custom attribute ${i + 1} value is required`);
        return false;
      }

      // Check for duplicate keys
      const duplicateKey = customAttributes.find((other, j) =>
        j !== i && other.key.trim() === attr.key.trim()
      );
      if (duplicateKey) {
        toast.error(`Duplicate custom attribute key: ${attr.key}`);
        return false;
      }

      // Check if custom key conflicts with predefined keys
      const conflictingField = configFields.find(field => field.key === attr.key.trim());
      if (conflictingField) {
        toast.error(`Custom attribute key "${attr.key}" conflicts with predefined field`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      // In edit mode, use the original phone number; in create mode, construct it
      const phoneNo = integration ? integration.phoneNo : formData.isdCode + formData.phoneNumber;

      // Combine predefined config values with custom attributes
      const allDetails = { ...configValues };
      customAttributes.forEach(attr => {
        if (attr.key.trim() && attr.value.trim()) {
          allDetails[attr.key.trim()] = attr.value.trim();
        }
      });

      const requestData: any = {
        agentType: formData.agentType,
        intType: formData.intType,
        phoneNo,
        countryCode: integration ? integration.countryCode : formData.countryCode,
        details: allDetails,
      };

      // Include ID for edit mode
      if (integration?.id) {
        requestData.id = integration.id;
      }

      // Trim all keys and values before sending to backend
      const trimmedRequestData = trimCommunicationIntegrationFormData(requestData);

      if (integration?.id) {
        //Edit
        await customInstance({
          url: API_CONFIG.ENDPOINTS.COMMUNICATION_INTEGRATION,
          method: "PUT",
          data: trimmedRequestData,
        });
      } else {
        //Create
        await customInstance({
          url: API_CONFIG.ENDPOINTS.COMMUNICATION_INTEGRATION,
          method: "POST",
          data: trimmedRequestData,
        });
      }


      toast.success(
        integration
          ? "Communication integration updated successfully"
          : "Communication integration created successfully"
      );
      onSuccess();
    } catch (error: any) {
      console.error("Error saving phone number mapping:", error);
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
        <CardHeader className="pb-5">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-foreground">
                Phone Number Mapping Configuration
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1.5">
                Set up the Phone Number Mapping parameters and settings
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Agent Type */}
            <div className="space-y-2.5">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/5 rounded-md">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                </div>
                <span>Agent Type</span>
                <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
              </label>
              <Select
                value={formData.agentType}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, agentType: value }))
                }
                required
                disabled={!!integration}
              >
                <SelectTrigger className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80">
                  <SelectValue placeholder="Select Agent Type..." />
                </SelectTrigger>
                <SelectContent>
                  {AGENT_TYPE.map((type) => (
                    <SelectItem key={type} value={type}>
                      {AGENT_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Integration Type */}
            <div className="space-y-2.5">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/5 rounded-md">
                  <Settings className="h-3.5 w-3.5 text-primary" />
                </div>
                <span>Integration Type</span>
                <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
              </label>
              <Select
                value={formData.intType}
                onValueChange={handleIntTypeChange}
                required
                disabled={!!integration}
              >
                <SelectTrigger className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80">
                  <SelectValue placeholder="Select Integration Type..." />
                </SelectTrigger>
                <SelectContent>
                  {INTEGRATION_TYPES.map((type) => (
                    <SelectItem key={type.key} value={type.key}>
                      {type.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phone Number */}
            <div className="space-y-2.5 md:col-span-2">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/5 rounded-md">
                  <Phone className="h-3.5 w-3.5 text-primary" />
                </div>
                <span>Phone Number</span>
                <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
              </label>
              {integration ? (
                // Edit mode - display in separate text boxes
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2.5">
                    <label className="text-sm font-medium text-foreground">Country Code</label>
                    <input
                      type="text"
                      className="h-11 w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm text-muted-foreground"
                      value={formData.countryCode}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="space-y-2.5">
                    <label className="text-sm font-medium text-foreground">Phone Number</label>
                    <input
                      type="text"
                      className="h-11 w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm text-muted-foreground"
                      value={integration.phoneNo}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              ) : (
                // Create mode - editable phone number
                <div className="flex gap-2">
                  {isLoadingCountries ? (
                    <div className="w-48 h-11 flex items-center px-4 py-2.5 border border-border bg-muted rounded-lg text-muted-foreground">
                      Loading...
                    </div>
                  ) : (
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => {
                        const selectedCountry = countries.find(c => c.countryCode === value);
                        if (selectedCountry) {
                          setFormData((prev) => ({
                            ...prev,
                            countryCode: selectedCountry.countryCode,
                            isdCode: selectedCountry.isdCode,
                          }));
                        }
                      }}
                    >
                      <SelectTrigger className="w-48 h-11 rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80">
                        <SelectValue placeholder="Select country..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        {countries.map((country) => (
                          <SelectItem key={country.countryCode} value={country.countryCode}>
                            {country.countryCode} ({country.isdCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <input
                    type="tel"
                    className="flex-1 h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                    placeholder="Enter phone number..."
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData((prev) => ({ ...prev, phoneNumber: value }));
                    }}
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Configuration Fields */}
          {isLoadingConfig && (
            <div className="text-center py-4">
              <p className="text-muted-foreground">Loading configuration fields...</p>
            </div>
          )}

          {configFields.length > 0 && (
            <div className="space-y-6 pt-4 border-t border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Configuration Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {configFields.map((field) => (
                  <div key={field.key} className="space-y-2.5">
                    <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                      <div className="p-1.5 bg-primary/5 rounded-md">
                        <Key className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <span>{field.name}</span>
                      <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                    </label>
                    <input
                      type="text"
                      className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                      placeholder={`Enter ${field.name}...`}
                      value={configValues[field.key] || ""}
                      onChange={(e) => handleConfigValueChange(field.key, e.target.value)}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Attributes */}
          {allowCustomAttributes && (
            <div className="space-y-6 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Custom Attributes</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCustomAttribute}
                  className="h-9"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Attribute
                </Button>
              </div>

              {customAttributes.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No custom attributes added. Click "Add Attribute" to add custom key-value pairs.
                </p>
              ) : (
                <div className="space-y-4">
                  {customAttributes.map((attr, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <div className="space-y-2.5">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                          <div className="p-1.5 bg-primary/5 rounded-md">
                            <Key className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>Key</span>
                          <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                        </label>
                        <input
                          type="text"
                          className="h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                          placeholder="Enter attribute key..."
                          value={attr.key}
                          onChange={(e) => handleCustomAttributeChange(index, 'key', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2.5">
                        <label className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                          <div className="p-1.5 bg-primary/5 rounded-md">
                            <Hash className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>Value</span>
                          <Badge variant="destructive" className="text-xs px-2 py-0.5 font-medium">Required</Badge>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="flex-1 h-11 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-input/80"
                            placeholder="Enter attribute value..."
                            value={attr.value}
                            onChange={(e) => handleCustomAttributeChange(index, 'value', e.target.value)}
                            required
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveCustomAttribute(index)}
                            className="h-11 px-3"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="h-11 px-6"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSaving}
          className="h-11 px-6 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200"
        >
          {isSaving ? "Saving..." : integration ? "Update Integration" : "Create Integration"}
        </Button>
      </div>
    </form>
  );
};

export default CommunicationIntegrationForm;